// Curated-collection browser inside Study (plan Item D2). `collectionScreen`
// lists the games in a vendored collection; `collectionGameScreen` opens one on
// the full engine study board (`mountFromPgn`). Both re-fetch + re-parse the
// collection's `.pgn` (memoised in `collections.ts`), so a slug+index fully
// addresses a game and the URLs are reload-safe.

import { esc, h } from "../dom.ts";
import { t, tp } from "../i18n/index.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { currentUser } from "../session.ts";
import { currentToken, navigate } from "../router.ts";
import { topNav } from "../ui/topnav.ts";
import { buildReview } from "../game/review.ts";
import { pgnResultLine } from "../game/pgn.ts";
import { collectionDescription, collectionTitle, loadCollection } from "../game/collections.ts";
import { destroyActive, mountFromPgn } from "./analysis.ts";

export async function collectionScreen(slug: string): Promise<HTMLElement> {
  if (!currentUser()) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }

  let info, games;
  try {
    ({ info, games } = await loadCollection(slug));
  } catch {
    return notice(t("collections.notAvail"));
  }

  const description = collectionDescription(info);
  const el = h(`
    <main class="card wide">
      <section class="panel">
        <div class="panel-head">
          <h2>${esc(collectionTitle(info))}</h2>
          <a class="btn ghost" data-link href="/study">${t("collections.studyBack")}</a>
        </div>
        ${description ? `<p class="fine">${esc(description)}</p>` : ""}
        <div class="games collection-games"></div>
      </section>
    </main>
  `);
  el.prepend(topNav("study"));

  const list = el.querySelector<HTMLElement>(".games")!;
  if (games.length === 0) {
    list.innerHTML = `<p class="fine">${t("collections.emptyColl")}</p>`;
  } else {
    games.forEach((g, i) => {
      const a = document.createElement("a");
      a.className = "game-row";
      a.setAttribute("data-link", "");
      a.href = `/study/collection/${encodeURIComponent(slug)}/${i}`;
      const parts = [pgnResultLine(g.meta), tp("plural.moves", Math.ceil(g.moves.length / 2))];
      if (g.meta.opening) parts.push(g.meta.opening);
      else if (g.meta.eco) parts.push(g.meta.eco);
      a.innerHTML =
        `<span class="opp">${esc(g.meta.white)} vs ${esc(g.meta.black)}</span>` +
        `<span class="meta">${esc(parts.join(" · "))}</span>`;
      list.append(a);
    });
  }
  return el;
}

export async function collectionGameScreen(slug: string, idxRaw: string): Promise<HTMLElement> {
  if (!currentUser()) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }
  // Tear the previous board down now (before the fetch + parse), like the other
  // analysis entry points, so its engine doesn't run for the whole load.
  const token = currentToken();
  destroyActive();
  const idx = Number(idxRaw);
  let games;
  try {
    ({ games } = await loadCollection(slug));
  } catch {
    return notice(t("collections.notAvail"));
  }
  const g = games[idx];
  if (!g) return notice(t("collections.notInColl"));
  return mountFromPgn(buildReview(g.moves, g.initialFen), g.meta, "white", token);
}

function notice(body: string): HTMLElement {
  return h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="msg bad">${esc(body)}</p>
      <p><a class="btn ghost" data-link href="/study">${t("collections.backToStudy")}</a></p>
    </main>
  `);
}
